/**
 * AYBL Intelligent Preloader
 * Smart preloading system for collection pages with hover and scroll-speed detection
 */

class IntelligentPreloader {
  constructor() {
    this.preloadedUrls = new Set();
    this.preloadQueue = [];
    this.scrollMetrics = {
      lastScrollY: 0,
      scrollVelocity: 0,
      scrollDirection: 'down',
      lastScrollTime: Date.now()
    };
    
    this.config = {
      hoverDelay: 100,
      scrollVelocityThreshold: 2,
      maxScrollPreloads: 3,
      scrollPreloadDistance: 200,
      maxConcurrentPreloads: 2,
      preloadTimeout: 3000,
      connectionThreshold: 'slow-2g',
      batteryThreshold: 0.2,
      respectRateLimits: true,
      maxPreloadsPerMinute: 20,
      preloadHistory: []
    };
    
    this.isMobile = this.detectMobile();
    this.connectionSpeed = this.getConnectionSpeed();
    this.batteryLevel = null;
    
    this.init();
  }

  async init() {
    await this.checkBatteryStatus();
    
    if (!this.shouldEnablePreloading()) {
      return;
    }
    
    if (this.isMobile) {
      this.setupScrollPreloading();
    } else {
      this.setupHoverPreloading();
    }
    
    this.setupPerformanceMonitoring();
  }

  /**
   * Check if preloading should be enabled
   */
  shouldEnablePreloading() {
    // Disable on very slow connections
    if (this.connectionSpeed === 'slow-2g' || this.connectionSpeed === '2g') {
      return false;
    }
    
    // Disable on low battery
    if (this.batteryLevel !== null && this.batteryLevel < this.config.batteryThreshold) {
      return false;
    }
    
    // Check if we're hitting rate limits
    if (this.isRateLimited()) {
      return false;
    }
    
    return true;
  }

  setupHoverPreloading() {
    const setupCards = () => {
      const productCards = document.querySelectorAll('product-card, .product-card, .product-item, [data-product-url], [handle]');
      
      productCards.forEach((card) => {
        const productUrl = this.getProductUrl(card);
        if (!productUrl) return;
        
        let hoverTimeout;
        
        card.addEventListener('mouseenter', () => {
          clearTimeout(hoverTimeout);
          hoverTimeout = setTimeout(() => {
            this.preloadProduct(productUrl, 'hover');
          }, this.config.hoverDelay);
        });
        
        card.addEventListener('mouseleave', () => {
          clearTimeout(hoverTimeout);
        });
      });
    };
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupCards);
    } else {
      setupCards();
    }
  }

  setupScrollPreloading() {
    let scrollTimeout;
    let preloadedInSession = 0;
    
    const handleScroll = () => {
      const currentTime = Date.now();
      const currentScrollY = window.scrollY;
      
      const timeDelta = currentTime - this.scrollMetrics.lastScrollTime;
      const scrollDelta = currentScrollY - this.scrollMetrics.lastScrollY;
      
      if (timeDelta > 0) {
        this.scrollMetrics.scrollVelocity = Math.abs(scrollDelta) / timeDelta;
        this.scrollMetrics.scrollDirection = scrollDelta > 0 ? 'down' : 'up';
      }
      
      this.scrollMetrics.lastScrollY = currentScrollY;
      this.scrollMetrics.lastScrollTime = currentTime;
      
      clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(() => {
        this.handleScrollStop(preloadedInSession);
        preloadedInSession = 0;
      }, 150);
    };
    
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  handleScrollStop(preloadedInSession) {
    if (this.scrollMetrics.scrollVelocity > this.config.scrollVelocityThreshold || 
        preloadedInSession >= this.config.maxScrollPreloads) {
      return;
    }
    
    const productsToPreload = this.getProductsNearViewport();
    const remainingPreloads = this.config.maxScrollPreloads - preloadedInSession;
    const productsToProcess = productsToPreload.slice(0, remainingPreloads);
    
    productsToProcess.forEach(({ url, element }) => {
      this.preloadProduct(url, 'scroll');
      preloadedInSession++;
    });
  }

  getProductsNearViewport() {
    const productCards = document.querySelectorAll('.product-card, .product-item, [data-product-url]');
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;
    const preloadZoneTop = scrollY + viewportHeight;
    const preloadZoneBottom = preloadZoneTop + this.config.scrollPreloadDistance;
    
    const nearbyProducts = [];
    
    productCards.forEach(card => {
      const rect = card.getBoundingClientRect();
      const elementTop = rect.top + scrollY;
      
      if (elementTop >= preloadZoneTop && elementTop <= preloadZoneBottom) {
        const productUrl = this.getProductUrl(card);
        if (productUrl && !this.preloadedUrls.has(productUrl)) {
          nearbyProducts.push({ url: productUrl, element: card });
        }
      }
    });
    
    return nearbyProducts;
  }

  getProductUrl(card) {
    if (card.tagName === 'PRODUCT-CARD' && card.getAttribute('handle')) {
      return `/products/${card.getAttribute('handle')}`;
    }
    
    const link = card.querySelector('a[href*="/products/"]') || 
                 card.querySelector('[data-product-url]') ||
                 (card.tagName === 'A' && card.href && card.href.includes('/products/') ? card : null);
    
    if (link) {
      return link.href || link.dataset.productUrl;
    }
    
    if (card.getAttribute && card.getAttribute('handle')) {
      return `/products/${card.getAttribute('handle')}`;
    }
    
    return null;
  }

  async preloadProduct(url, trigger = 'unknown') {
    if (this.preloadedUrls.has(url) || this.preloadQueue.includes(url)) {
      return;
    }
    
    if (this.isRateLimited()) {
      return;
    }
    
    this.preloadQueue.push(url);
    this.preloadedUrls.add(url);
    this.trackPreload();
    
    try {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Preload timeout')), this.config.preloadTimeout);
      });
      
      const preloadPromise = new Promise((resolve, reject) => {
        link.onload = resolve;
        link.onerror = reject;
        document.head.appendChild(link);
      });
      
      await Promise.race([preloadPromise, timeoutPromise]);
      
    } catch (error) {
      this.preloadedUrls.delete(url);
    } finally {
      const queueIndex = this.preloadQueue.indexOf(url);
      if (queueIndex > -1) {
        this.preloadQueue.splice(queueIndex, 1);
      }
    }
  }

  isRateLimited() {
    if (!this.config.respectRateLimits) return false;
    
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    this.config.preloadHistory = this.config.preloadHistory.filter(time => time > oneMinuteAgo);
    
    return this.config.preloadHistory.length >= this.config.maxPreloadsPerMinute;
  }

  trackPreload() {
    this.config.preloadHistory.push(Date.now());
  }

  detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  }

  getConnectionSpeed() {
    if ('connection' in navigator) {
      return navigator.connection.effectiveType || 'unknown';
    }
    return 'unknown';
  }

  async checkBatteryStatus() {
    if ('getBattery' in navigator) {
      try {
        const battery = await navigator.getBattery();
        this.batteryLevel = battery.level;
        
        battery.addEventListener('levelchange', () => {
          this.batteryLevel = battery.level;
        });
      } catch (error) {
        // Battery API not available
      }
    }
  }

  setupPerformanceMonitoring() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (entry.entryType === 'navigation' && entry.loadEventEnd > 3000) {
            this.config.maxConcurrentPreloads = Math.max(1, this.config.maxConcurrentPreloads - 1);
          }
        });
      });
      
      observer.observe({ entryTypes: ['navigation'] });
    }
  }

  getStats() {
    return {
      preloadedUrls: this.preloadedUrls.size,
      queueLength: this.preloadQueue.length,
      isMobile: this.isMobile,
      connectionSpeed: this.connectionSpeed,
      batteryLevel: this.batteryLevel,
      scrollVelocity: this.scrollMetrics.scrollVelocity,
      recentPreloads: this.config.preloadHistory.length,
      rateLimited: this.isRateLimited()
    };
  }

  forcePreload(url) {
    return this.preloadProduct(url, 'manual');
  }
}

const intelligentPreloader = new IntelligentPreloader();

window.AYBL_IntelligentPreloader = {
  preloader: intelligentPreloader,
  getStats: () => intelligentPreloader.getStats(),
  forcePreload: (url) => intelligentPreloader.forcePreload(url)
};
